import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LangContext";
import { Lock, TicketPercent } from "lucide-react";

/**
 * OrderModal – supports single-program checkout AND the "Mastery & Strength Combo".
 * Pass `combo` prop as the combo descriptor to trigger the combo Stripe endpoint.
 */
export default function OrderModal({ open, onClose, program, combo }) {
    const { t } = useLang();
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", promo_code: "" });
    const [loading, setLoading] = useState(false);

    const isCombo = !!combo;
    const displayTitle = isCombo ? combo.title : program?.title;
    const displayPrice = isCombo ? combo.price : program?.price_eur;
    const displayOriginal = isCombo ? combo.original : null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const payload = {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            email: form.email.trim().toLowerCase(),
            promo_code: form.promo_code.trim().toUpperCase() || null,
        };
        try {
            const url = isCombo
                ? "/checkout/create-combo-session"
                : "/checkout/create-session";
            const body = isCombo ? payload : { ...payload, program_id: program.id };
            const { data } = await api.post(url, body);
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="order-modal"
                           className="bg-zinc-950 border-white/10 rounded-none max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">
                        {isCombo ? "Combo Checkout" : t("order_title")}
                    </DialogTitle>
                    <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="text-white/70">{displayTitle}</span>
                        <span className="text-volt font-heading text-xl flex items-baseline gap-2">
                            {displayOriginal && (
                                <span className="text-white/40 line-through text-sm">
                                    {displayOriginal.toFixed(2)} €
                                </span>
                            )}
                            {displayPrice?.toFixed(2)} €
                        </span>
                    </div>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-white/60">{t("order_first")}</Label>
                            <Input required data-testid="order-first"
                                   value={form.first_name}
                                   onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                   className="mt-1 bg-black border-white/10 focus:border-volt rounded-none" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-white/60">{t("order_last")}</Label>
                            <Input required data-testid="order-last"
                                   value={form.last_name}
                                   onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                   className="mt-1 bg-black border-white/10 focus:border-volt rounded-none" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-white/60">{t("order_email")}</Label>
                        <Input required type="email" data-testid="order-email"
                               value={form.email}
                               onChange={(e) => setForm({ ...form, email: e.target.value })}
                               className="mt-1 bg-black border-white/10 focus:border-volt rounded-none" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                            <TicketPercent size={12} className="text-volt" /> Promo Code (optional)
                        </Label>
                        <Input data-testid="order-promo"
                               placeholder="EPA-XXXXXXXX"
                               value={form.promo_code}
                               onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                               className="mt-1 bg-black border-white/10 focus:border-volt rounded-none font-mono tracking-wider" />
                    </div>
                    <p className="text-xs text-white/40 flex items-center gap-1.5">
                        <Lock size={11} /> Secure payment via Stripe · we'll email your PDF after payment
                    </p>
                    <DialogFooter className="flex gap-2 sm:justify-end">
                        <button type="button" onClick={onClose}
                                className="btn-ghost-outline px-5 py-2.5 text-sm">
                            {t("order_cancel")}
                        </button>
                        <button type="submit" disabled={loading}
                                data-testid="order-submit"
                                className="btn-volt px-6 py-2.5 text-sm disabled:opacity-60">
                            {loading ? "…" : t("order_pay")}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
